import React from 'react';
import {
    Box,
    Typography,
    Chip,
    Tooltip,
    Divider,
    Button
} from '@mui/material';
import {
    ContentCopy as CopyIcon,
    Edit as EditIcon,
    WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import PopupBase from '../../../popup/base/PopupBase';
import CustomButton from '../../../components/buttons/CustomButton';
import MessagesTemplate from '../../../consts/MessagesTemplate';
import fillTemplateExample from './fillTemplateExample';

const TemplatePreviewModal = ({
    open,
    onClose,
    template,
    onEdit
}) => {
    if (!template || !open) return null;

    const handleCopy = async () => {
        try {
            // Para copiar, usamos o formato WhatsApp correto
            const formattedContent = getFormattedPreview(template.TemplateContent);
            const contentToCopy = MessagesTemplate.ConvertToWhatsAppText(formattedContent);
            await navigator.clipboard.writeText(contentToCopy);
            // Aqui você pode adicionar uma notificação de sucesso
        } catch {}
    };

    // Para o preview visual, mantemos as quebras de linha normais
    // Não usamos ConvertToWhatsAppText aqui pois ele converte para %0a (formato URL)
    const getFormattedPreview = (content) => fillTemplateExample(content);

    const getTemplateDisplayName = (key) => {
        const displayNames = {
            'AppointmentCreatedFullMessage': 'Confirmação de Agendamento (Completa)',
            'AppointmentCreatedShortMessage': 'Confirmação de Agendamento (Resumida)',
            'AppointmentAskForConfirmation1': 'Primeira Confirmação',
            'AppointmentAskForConfirmation2': 'Segunda Confirmação',
            'AppointmentAskForConfirmation3': 'Terceira Confirmação',
            'Appointment7DaysReminder': 'Lembrete 7 Dias',
            'AppointmentReservationMessage': 'Reserva de Horário',
            'AppointmentReservationReminder': 'Lembrete de Cadastro da Reserva',
            'AppointmentReservationReleased': 'Reserva Liberada',
            'AppointmentCanceledMessage': 'Cancelamento de Agendamento',
            'AppointmentCancellationDueLackOfReturn': 'Cancelamento por Falta de Retorno',
            'SuggestDate': 'Sugestão de Data',
            'HappyBirthday': 'Feliz Aniversário',
            'SuggestDateFromWaitingList': 'Sugestão da Lista de Espera',
            'AppointmentRescheduledConfirmedMessage': 'Reagendamento Confirmado',
            'CanceledAppointmentFollowUp': 'Follow-up Agendamento Cancelado',
            'FirstAppointmentFollowup': 'Follow-up Primeiro Agendamento',
            'NPSFeedbackRequest': 'Solicitação de Feedback NPS',
            'GoogleReviewRequest': 'Solicitação de Avaliação Google',
            'AppointmentAssessmentTemplate': 'Avaliação do Agendamento',
            'AppointmentEvolutionTemplate': 'Evolução do Tratamento'
        };
        
        return displayNames[key] || key;
    };

    // Conteúdo do popup
    const getContent = () => (
        <Box>
            {/* Informações do template */}
            <Box sx={{ mb: 3 }}>
                {/* Nome do template */}
                <Typography
                    variant="h6"
                    component="div"
                    sx={{
                        fontWeight: 500,
                        mb: 1,
                        color: 'text.primary'
                    }}
                >
                    {getTemplateDisplayName(template.TemplateKey)}
                </Typography>
                
                {/* Status como chip */}
                <Box sx={{ mb: 2 }}>
                    {template.isFallback ? (
                        <Chip
                            label="Padrão"
                            size="small"
                            color="primary"
                            variant="filled"
                            sx={{ fontSize: '0.75rem' }}
                        />
                    ) : (
                        <Chip
                            label="Customizada"
                            size="small"
                            color="secondary"
                            variant="filled"
                            sx={{ fontSize: '0.75rem' }}
                        />
                    )}
                </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Header do Preview WhatsApp */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <WhatsAppIcon color="success" />
                <Typography variant="subtitle1" fontWeight={600}>
                    Preview WhatsApp
                </Typography>
            </Box>

            {/* Preview do WhatsApp */}
            <Box sx={{
                backgroundColor: '#f5f5f5',
                borderRadius: 2,
                p: 3,
                border: '1px solid #e0e0e0',
                fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
                whiteSpace: 'pre-wrap',
                lineHeight: 1.6,
                minHeight: '200px'
            }}>
                <Typography 
                    variant="body1" 
                    component="div"
                    sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontSize: '14px'
                    }}
                >
                    {getFormattedPreview(template.TemplateContent)}
                </Typography>
            </Box>
        </Box>
    );

    // Footer com botões de ação
    const getFooter = () => (
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Tooltip title="Copiar conteúdo">
                <Button
                    startIcon={<CopyIcon />}
                    onClick={handleCopy}
                    variant="outlined"
                    color="secondary"
                >
                    Copiar
                </Button>
            </Tooltip>
            
            <CustomButton
                startIcon={<EditIcon />}
                onClick={() => {
                    onEdit && onEdit(template);
                    onClose();
                }}
                variant="contained"
                color="primary"
            >
                Editar
            </CustomButton>
        </Box>
    );

    return (
        <PopupBase
            Title="Visualizar modelo"
            Content={getContent()}
            Footer={getFooter()}
            OnClose={onClose}
            maxWidth="md"
        />
    );
};

export default TemplatePreviewModal;